'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { stegaClean } from '@sanity/client/stega';
import type { ITopBanner } from '@/sanity/lib/definitions';
import { resolveI18nText } from '@/sanity/globals/I18n';
import { resolvePageLink } from '@/sanity/globals/CTA';
import { useBannerCountdown } from '@/hooks/useBannerCountdown';
import { getCountdownLabels, pad2 } from '@/lib/bannerCountdown';

/**
 * Top banner — "New UI 2026" design. Rendered by
 * components/global/TopBanner.tsx when the document's Design field is "v1";
 * every other banner keeps TopBannerClassic.
 *
 * Built 1:1 from two Figma frames of "IQ Capital | New UI 2026":
 *   desktop 7625:13315 "V1 | Banner", 1920x64 — one row
 *   mobile  8662:23077 "V1 | Banner", 430 wide — two rows, 16px padding
 * `lg:` is 993px here (tailwind.config.ts), so the mobile frame is the base
 * and the desktop frame is the `lg:` override.
 *
 * Sizing: 1rem = 8px at the design width (app/globals.scss), so every Figma px
 * below is written as px / 8 in an arbitrary Tailwind value — 64px -> 8rem,
 * 20px -> 2.5rem, 5px -> 0.625rem.
 *
 * Fonts: Blacker Display ships only 300/400 here, so Figma's "Blacker Display
 * Bold" is approximated with `font-blacker font-bold` (the browser synthesises
 * the weight) and "Blacker Display Medium" with `font-blacker font-normal`.
 */

interface TopBannerV1Props {
  data: ITopBanner;
  language?: string;
}

interface TextSegment {
  text: string;
  highlighted: boolean;
}

/**
 * Shown next to the bolt when the document leaves Pre Text empty — the
 * campaign this design was drawn for, not the old "Flash sale". Editors
 * override it per language with Pre Text + its translations (German:
 * "Quarterly Angebot").
 */
const DEFAULT_PRE_TEXT = 'Quarterly Offer';

/**
 * The banner copy uses the same [[...]] markers as the classic banner:
 * "[[80% OFF]] Instant Funding & Challenges". Odd indexes of the split are the
 * captured groups, i.e. the highlighted segments (Figma 7625:13325 "80% OFF"),
 * even indexes the plain ones (7625:13327). Empty segments are dropped so the
 * 1px divider only ever falls between two real pieces of text.
 */
function parseTextSegments(text: string): TextSegment[] {
  return text
    .split(/\[\[(.+?)\]\]/g)
    .map((part, index) => ({ text: part.trim(), highlighted: index % 2 === 1 }))
    .filter((segment) => segment.text.length > 0);
}

export default function TopBannerV1({ data, language }: TopBannerV1Props) {
  const preText = resolveI18nText(data.preText, data.preTextI18n, language);
  // stegaClean before the regex split: in draft mode Sanity interleaves
  // invisible characters into the string and `[[` would no longer match.
  const text = stegaClean(resolveI18nText(data.text, data.textI18n, language)) ?? '';
  const ctaLabel = resolveI18nText(data.ctaLabel, data.ctaLabelI18n, language);
  // CTA Label is optional in Sanity: with a link but no label the pill would
  // hold nothing but the aria-hidden arrow, i.e. a link with no accessible
  // name (WCAG 2.4.4 / 4.1.2). stegaClean because this is an attribute value.
  const ctaFallbackLabel = stegaClean(preText) || DEFAULT_PRE_TEXT;
  const segments = parseTextSegments(text);

  // null while the countdown window is closed -> no countdown at all.
  const timeLeft = useBannerCountdown(data);
  const countdownUnits = timeLeft
    ? [timeLeft.days, timeLeft.hours, timeLeft.minutes, timeLeft.seconds]
    : null;
  // The V1 design has no unit captions, so the digits only make sense to a
  // screen reader through the group's label.
  const countdownLabels = getCountdownLabels(language);

  // Same deferred link resolution as TopBannerClassic.
  const link = data.ctaLink ? resolvePageLink(data.ctaLink, language) : null;
  const [finalLink, setFinalLink] = useState('');

  useEffect(() => {
    if (!link) return;
    setFinalLink(link);
  }, [link]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 'calc(-100% - 1.5rem)' }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
      className="relative w-full overflow-clip lg:min-h-[8rem]"
    >
      {/* decorative layers — Figma paints them edge to edge behind the content */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        {/* V1 | Banner background fill (vertical dark-green gradient). The two
            frames export their own plate — 1920x64 desktop (7625:13315) and
            430x100 mobile (8662:23077) — and the mobile one is the taller,
            two-row gradient, so each breakpoint gets its own. */}
        {/* eslint-disable-next-line @next/next/no-img-element -- decorative
            plate stretched to the bar; next/image would add no value. */}
        <img
          src="/promo-v1/banner-bg-mobile.webp"
          alt=""
          className="absolute left-0 top-0 h-full w-full max-w-none object-cover lg:hidden"
        />
        {/* eslint-disable-next-line @next/next/no-img-element -- decorative */}
        <img
          src="/promo-v1/banner-bg.webp"
          alt=""
          className="absolute left-0 top-0 hidden h-full w-full max-w-none object-cover lg:block"
        />

        {/* halftone noise — Figma Rectangle 1430108064, exclusion @ 10%.
            Figma stretches this fill to the bar (no object-fit), so the plate
            is squashed to the bar's aspect: `object-fill`, not `object-cover`
            (cover would crop a band out of the middle and blow the dots up). */}
        <div className="absolute inset-0 overflow-hidden opacity-10 mix-blend-exclusion">
          {/* eslint-disable-next-line @next/next/no-img-element -- decorative */}
          <img
            src="/promo-v1/noise.webp"
            alt=""
            className="absolute left-0 top-0 h-full w-full max-w-none object-fill"
          />
        </div>

        {/* angel, far left — Figma 7625:13322 "image 756": 324x404 box at
            x -87 / y -38, screen blend, image fill 124.69% wide */}
        <div className="absolute left-[-10.875rem] top-[-4.75rem] hidden h-[50.5rem] w-[40.5rem] mix-blend-screen lg:block">
          <div className="absolute inset-0 overflow-hidden">
            {/* eslint-disable-next-line @next/next/no-img-element -- decorative */}
            <img
              src="/promo-v1/angel.webp"
              alt=""
              className="absolute left-0 top-0 h-full w-[124.69%] max-w-none"
            />
          </div>
        </div>

        {/* angel, far right — Figma 7625:13323 "image 757": same 324x404 asset
            at x 1685 / y -40, rotate(180) + flip Y == mirrored horizontally.
            1920 - (1685 + 324) = -89px, anchored to the right edge so it holds
            at any viewport width. (The chess knight "image 738" sits at
            x 2117, fully outside the 1920 frame — intentionally skipped.) */}
        <div className="absolute right-[-11.125rem] top-[-5rem] hidden h-[50.5rem] w-[40.5rem] -scale-x-100 mix-blend-screen lg:block">
          <div className="absolute inset-0 overflow-hidden">
            {/* eslint-disable-next-line @next/next/no-img-element -- decorative */}
            <img
              src="/promo-v1/angel.webp"
              alt=""
              className="absolute left-0 top-0 h-full w-[124.69%] max-w-none"
            />
          </div>
        </div>
      </div>
      {/* end decorative layers */}

      {/* Content box. Figma insets the three groups 304px from both edges at
          1920 => 1312px of content. max-w 172rem (1376px) minus the 4rem
          (32px) side padding lands on exactly 1312px at 1920, while keeping
          breathing room between 993px and 1376px. */}
      <div className="relative z-[1] mx-auto flex w-full max-w-[172rem] flex-col gap-[1.75rem] p-[2rem] lg:min-h-[8rem] lg:flex-row lg:items-center lg:gap-[2.25rem] lg:px-[4rem] lg:py-0">
        {/* Mobile row 1 — Figma 8662:23079 (the "V1 | Banner" mobile frame,
            8662:23077, 430x?): label on the left, offer copy on the right, the
            whole row 16px inside the bar. `lg:contents` dissolves both row
            wrappers on desktop, so the four groups become columns of the bar
            itself in the 7625:13315 order: label | copy · countdown | CTA. The
            two outer groups take `lg:flex-1`, which keeps the middle pair
            optically centred exactly as the desktop frame has it.
            The campaign label is hidden below lg — the phone bar carries the
            offer copy, the countdown and the CTA, and the label is the piece
            the small screen can do without. The copy therefore keeps the
            frame's own 16px/14px sizes and wraps when a translation runs long
            (the German line is ~428px against 398px of frame). */}
        <div className="flex w-full flex-wrap items-center justify-between gap-x-[1rem] gap-y-[1rem] lg:contents">
          {/* Figma 8662:23127 / 7625:13316 — bolt + label, gap 6.751px */}
          <div className="hidden shrink-0 items-center gap-[0.84rem] lg:order-1 lg:flex lg:flex-1">
            {/* eslint-disable-next-line @next/next/no-img-element -- static icon */}
            <img
              src="/promo-v1/bolt.svg"
              alt=""
              aria-hidden
              className="h-[2rem] w-[2rem] shrink-0"
            />
            {/* Blacker Display Italic — 22px / -0.66px (7625:13320) */}
            <span className="whitespace-nowrap font-blacker text-[2.75rem] font-normal italic leading-normal tracking-[-0.0825rem] text-white">
              {preText || DEFAULT_PRE_TEXT}
            </span>
          </div>

          {/* Figma 8662:23134 / 7625:13324 — copy: highlighted segment, rule,
              plain segment. Mobile sets the pieces 15px/13px against the
              desktop 20px, and spaces them 8px against the desktop 18px. */}
          {segments.length > 0 && (
            <p className="m-0 flex min-w-0 flex-wrap items-center gap-x-[1.375rem] gap-y-[0.25rem] text-left lg:order-2 lg:gap-x-[2.25rem] lg:gap-y-[1rem]">
              {segments.map((segment, index) => (
                /* not `contents`: the rule must wrap together with the
                   segment it precedes, never dangle at the end of a line. The
                   inner gap repeats the <p>'s, so all three items keep the
                   frame's spacing. */
                <span
                  key={`${index}-${segment.text}`}
                  className="flex min-w-0 items-center gap-x-[1.375rem] lg:gap-x-[2.25rem]"
                >
                  {index > 0 && (
                    /* Figma 8662:23136 — 1px x 16.32px rule (desktop
                       7625:13326: 22.5px), white 30% */
                    <span
                      aria-hidden
                      className="h-[1.875rem] w-[0.125rem] shrink-0 bg-white/30 lg:h-[2.8125rem]"
                    />
                  )}
                  <span
                    className={
                      segment.highlighted
                        ? // 8662:23135 "80% OFF" — Blacker Bold (faux), 16/21.759, -0.48px
                          'font-blacker text-[2rem] font-bold leading-[2.72rem] tracking-[-0.06rem] text-white lg:text-[2.5rem] lg:leading-[3.75rem] lg:tracking-[-0.075rem]'
                        : // 8662:23137 — Blacker Medium approximated with 400, 14px, -0.42px
                          'font-blacker text-[1.75rem] font-normal leading-normal tracking-[-0.0525rem] text-white lg:text-[2.5rem] lg:tracking-[-0.075rem]'
                    }
                  >
                    {segment.text}
                  </span>
                </span>
              ))}
            </p>
          )}
        </div>

        {/* Mobile row 2 — Figma 8662:23087: countdown left, CTA right. Both
            keep their desktop metrics; only the copy above scales down. */}
        <div className="flex w-full flex-wrap items-center justify-between gap-x-[1.5rem] gap-y-[1rem] lg:contents">
          {/* Figma 8662:23090 / 7652:18600 — four glass boxes, gap 2px, ":"
              between them. No unit labels in this design. */}
          {countdownUnits && (
            /* items-start, as Figma 7652:18601: the ":" has no box, so its
               line box starts flush with the capsule's top border and the
               glyph sits slightly above the digits' optical centre. */
            <div
              role="timer"
              aria-label={countdownUnits
                .map((unit, index) => `${unit} ${countdownLabels[index]}`)
                .join(', ')}
              className="flex shrink-0 items-start gap-[0.25rem] whitespace-nowrap lg:order-3"
            >
              {countdownUnits.map((unit, index) => (
                <span key={index} className="contents">
                  {index > 0 && (
                    <span aria-hidden className="font-gestura text-[2.5rem] font-bold leading-[3.75rem] tracking-[-0.075rem] text-white">
                      :
                    </span>
                  )}
                  {/* px 5 / py 2 / radius 6 / blur 9.9px (Figma 8662:23091) */}
                  <span
                    aria-hidden
                    className="flex flex-col items-center justify-center rounded-[0.75rem] border border-white/10 bg-white/10 px-[0.625rem] py-[0.25rem] backdrop-blur-[9.9px]"
                  >
                    <span className="text-center font-gestura text-[2.5rem] font-bold leading-[3.75rem] tracking-[-0.075rem] tabular-nums text-white">
                      {pad2(unit)}
                    </span>
                  </span>
                </span>
              ))}
            </div>
          )}

          {/* Figma 8662:23102 / 7625:13328 "Link" — white pill, radius 35 */}
          {finalLink && (
            <div className="ml-auto flex shrink-0 lg:order-4 lg:ml-0 lg:flex-1 lg:justify-end">
              <Link
                href={finalLink}
                aria-label={ctaLabel ? undefined : ctaFallbackLabel}
                className="flex w-fit items-center gap-[1rem] rounded-full bg-white px-[2rem] py-[1rem] transition-opacity hover:opacity-80"
              >
                {ctaLabel && (
                  <span className="whitespace-nowrap font-geist text-[1.75rem] font-semibold leading-[2.625rem] tracking-[-0.0175rem] text-[#04231A]">
                    {ctaLabel}
                  </span>
                )}
                {/* eslint-disable-next-line @next/next/no-img-element -- static icon */}
                <img
                  src="/promo-v1/arrow.svg"
                  alt=""
                  aria-hidden
                  className="h-[2.25rem] w-[2.25rem] shrink-0"
                />
              </Link>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
